import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { activityApi, reminderApi, type Reminder } from '@/services/api'
import { getActivity, type Activity } from '@/services/content'
import ProgressDashboard from '@/components/progress/ProgressDashboard'
import styles from './MyChild.module.css'
import Icon from '@/components/ui/Icon'

interface SavedActivity {
  id: number
  activity_id: string
  note?: string
  created_at: string
}

interface ActivityLog {
  id: number
  activity_id: string
  situation_id?: string
  feedback?: string
  completed: boolean
  created_at: string
}

export default function MyChild() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const childProfile = user?.child_profile

  const [savedActivities, setSavedActivities] = useState<SavedActivity[]>([])
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([])
  const [reminders, setReminders] = useState<Reminder[]>([])
  const [missedReminders, setMissedReminders] = useState<Reminder[]>([])
  const [activityDetails, setActivityDetails] = useState<Record<string, Activity>>({})
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [dismissingId, setDissmissingId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'saved' | 'progress' | 'history' | 'reminders'>('saved')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      try {
        const [savedRes, logsRes, remindersRes, missedRes] = await Promise.all([
          activityApi.getSaved(),
          activityApi.getLogs(),
          reminderApi.getReminders(),
          reminderApi.getMissedReminders(),
        ])
        setSavedActivities(savedRes.data || [])
        setActivityLogs(logsRes.data || [])
        setReminders(remindersRes.data || [])
        setMissedReminders(missedRes.data || [])

        // Load activity details for all saved, logged, and reminder activities
        const activityIds = new Set([
          ...((savedRes.data || []) as SavedActivity[]).map((s: SavedActivity) => s.activity_id),
          ...((logsRes.data || []) as ActivityLog[]).map((l: ActivityLog) => l.activity_id),
          ...((remindersRes.data || []) as Reminder[]).map((r: Reminder) => r.activity_id),
          ...((missedRes.data || []) as Reminder[]).map((r: Reminder) => r.activity_id),
        ])

        const details: Record<string, Activity> = {}
        for (const id of activityIds) {
          const activity = await getActivity(id)
          if (activity) details[id] = activity
        }
        setActivityDetails(details)
      } catch {
        // Handle error silently
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  const handleUnsave = async (id: number) => {
    try {
      await activityApi.unsaveActivity(String(id))
      setSavedActivities(prev => prev.filter(s => s.id !== id))
    } catch {
      // Handle error
    }
  }

  const handleDeleteReminder = async (id: string) => {
    setDeletingId(id)
    try {
      await reminderApi.deleteReminder(id)
      setReminders(prev => prev.filter(r => r.id !== id))
    } catch {
      alert('Failed to delete reminder. Please try again.')
    } finally {
      setDeletingId(null)
    }
  }

  const handleDismissReminder = async (id: string) => {
    setDissmissingId(id)
    try {
      await reminderApi.dismissReminder(id)
      setMissedReminders(prev => prev.filter(r => r.id !== id))
    } catch {
      alert('Failed to dismiss reminder. Please try again.')
    } finally {
      setDissmissingId(null)
    }
  }

  const handleRescheduleReminder = (reminder: Reminder) => {
    // Navigate to activity detail where user can set a new reminder
    navigate(`/activity/${reminder.activity_id}`)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const getRepeatLabel = (reminder: Reminder) => {
    if (!reminder.is_recurring) return ''
    switch (reminder.repeat_type) {
      case 'daily': return '(Daily)'
      case 'weekly': return '(Weekly)'
      case 'custom': return '(Custom)'
      default: return ''
    }
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>My Child</h1>

      {childProfile ? (
        <>
          <div className={styles.profile}>
            <div className={styles.avatar}>
              {childProfile.name.charAt(0).toUpperCase()}
            </div>
            <h2 className={styles.name}>{childProfile.name}</h2>
            <p className={styles.age}>
              {Math.floor(childProfile.age_months / 12)} years{' '}
              {childProfile.age_months % 12} months
            </p>
          </div>

          {childProfile.sensory_profile && Object.keys(childProfile.sensory_profile).length > 0 && (() => {
            const profile = childProfile.sensory_profile as { movement?: string; touch?: string; sound?: string }
            const getLabel = (value: string) => {
              if (value === 'seeking') return 'Seeker'
              if (value === 'avoiding') return 'Avoider'
              return 'Mixed'
            }
            return (
              <div className={styles.sensorySection}>
                <h3 className={styles.sensoryTitle}>Sensory Profile</h3>
                <div className={styles.sensoryGrid}>
                  {profile.movement && (
                    <div className={styles.sensoryItem}>
                      <span className={styles.sensoryIcon}></span>
                      <span className={styles.sensoryLabel}>Movement</span>
                      <span className={`${styles.sensoryValue} ${styles[profile.movement]}`}>
                        {getLabel(profile.movement)}
                      </span>
                    </div>
                  )}
                  {profile.touch && (
                    <div className={styles.sensoryItem}>
                      <span className={styles.sensoryIcon}></span>
                      <span className={styles.sensoryLabel}>Touch</span>
                      <span className={`${styles.sensoryValue} ${styles[profile.touch]}`}>
                        {getLabel(profile.touch)}
                      </span>
                    </div>
                  )}
                  {profile.sound && (
                    <div className={styles.sensoryItem}>
                      <span className={styles.sensoryIcon}></span>
                      <span className={styles.sensoryLabel}>Sound</span>
                      <span className={`${styles.sensoryValue} ${styles[profile.sound]}`}>
                        {getLabel(profile.sound)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}
        </>
      ) : (
        <div className={styles.placeholder}>
          <span className={styles.icon}><Icon name="baby" size={22} /></span>
          <p>No child profile yet</p>
        </div>
      )}

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'saved' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('saved')}
        >
          Saved ({savedActivities.length})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'progress' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('progress')}
        >
          Progress
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'reminders' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('reminders')}
        >
          Reminders ({reminders.length + missedReminders.length})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'history' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History ({activityLogs.length})
        </button>
      </div>

      {loading && activeTab !== 'progress' ? (
        <div className={styles.loading}>Loading...</div>
      ) : activeTab === 'progress' ? (
        <ProgressDashboard />
      ) : activeTab === 'reminders' ? (
        <div className={styles.list}>
          {/* Missed Reminders Section */}
          {missedReminders.length > 0 && (
            <div className={styles.missedSection}>
              <h3 className={styles.missedTitle}>Missed Reminders</h3>
              {missedReminders.map((reminder) => {
                const activity = activityDetails[reminder.activity_id]
                return (
                  <div key={reminder.id} className={styles.missedCard}>
                    <button
                      className={styles.cardContent}
                      onClick={() => navigate(`/activity/${reminder.activity_id}`)}
                    >
                      <h3 className={styles.cardTitle}>
                        {activity?.title || reminder.activity_title || reminder.activity_id}
                      </h3>
                      <p className={styles.cardMeta}>
                        Was scheduled for {formatDateTime(reminder.scheduled_at)}
                      </p>
                    </button>
                    <button
                      className={styles.rescheduleButton}
                      onClick={() => handleRescheduleReminder(reminder)}
                      aria-label="Reschedule"
                      title="Reschedule"
                    >
                      
                    </button>
                    <button
                      className={styles.dismissButton}
                      onClick={() => handleDismissReminder(reminder.id)}
                      disabled={dismissingId === reminder.id}
                      aria-label="Dismiss"
                    >
                      {dismissingId === reminder.id ? '...' : '✕'}
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Upcoming Reminders */}
          {reminders.length === 0 && missedReminders.length === 0 ? (
            <div className={styles.empty}>
              <span className={styles.emptyIcon}><Icon name="bell" size={22} /></span>
              <p>No reminders yet</p>
              <p className={styles.emptyHint}>
                Set reminders from activity pages to plan your routine
              </p>
            </div>
          ) : reminders.length === 0 ? null : (
            <>
              {missedReminders.length > 0 && (
                <h3 className={styles.upcomingTitle}>Upcoming</h3>
              )}
              {reminders.map((reminder) => {
                const activity = activityDetails[reminder.activity_id]
                return (
                  <div key={reminder.id} className={styles.card}>
                    <button
                      className={styles.cardContent}
                      onClick={() => navigate(`/activity/${reminder.activity_id}`)}
                    >
                      <h3 className={styles.cardTitle}>
                        {activity?.title || reminder.activity_title || reminder.activity_id}
                      </h3>
                      <p className={styles.cardMeta}>
                        {formatDateTime(reminder.scheduled_at)} {getRepeatLabel(reminder)}
                      </p>
                    </button>
                    <button
                      className={styles.unsaveButton}
                      onClick={() => handleDeleteReminder(reminder.id)}
                      disabled={deletingId === reminder.id}
                      aria-label="Delete reminder"
                    >
                      {deletingId === reminder.id ? '...' : '✕'}
                    </button>
                  </div>
                )
              })}
            </>
          )}
        </div>
      ) : activeTab === 'saved' ? (
        <div className={styles.list}>
          {savedActivities.length === 0 ? (
            <div className={styles.empty}>
              <span className={styles.emptyIcon}></span>
              <p>No saved activities yet</p>
              <p className={styles.emptyHint}>
                Save activities to access them quickly later
              </p>
            </div>
          ) : (
            savedActivities.map((saved) => {
              const activity = activityDetails[saved.activity_id]
              return (
                <div key={saved.id} className={styles.card}>
                  <button
                    className={styles.cardContent}
                    onClick={() => navigate(`/activity/${saved.activity_id}`)}
                  >
                    <h3 className={styles.cardTitle}>
                      {activity?.title || saved.activity_id}
                    </h3>
                    <p className={styles.cardMeta}>
                      {activity?.duration} min • Saved {formatDate(saved.created_at)}
                    </p>
                  </button>
                  <button
                    className={styles.unsaveButton}
                    onClick={() => handleUnsave(saved.id)}
                    aria-label="Remove from saved"
                  >
                    ✕
                  </button>
                </div>
              )
            })
          )}
        </div>
      ) : (
        <div className={styles.list}>
          {activityLogs.length === 0 ? (
            <div className={styles.empty}>
              <span className={styles.emptyIcon}><Icon name="clipboard-list" size={22} /></span>
              <p>No activity history yet</p>
              <p className={styles.emptyHint}>
                Complete activities to track your progress
              </p>
            </div>
          ) : (
            activityLogs.map((log) => {
              const activity = activityDetails[log.activity_id]
              return (
                <button
                  key={log.id}
                  className={styles.historyCard}
                  onClick={() => navigate(`/activity/${log.activity_id}`)}
                >
                  <div className={styles.historyInfo}>
                    <h3 className={styles.cardTitle}>
                      {activity?.title || log.activity_id}
                    </h3>
                    <p className={styles.cardMeta}>
                      {formatDate(log.created_at)}
                      {log.feedback && ` • ${log.feedback}`}
                    </p>
                  </div>
                  <span className={styles.historyStatus}>
                    {log.completed ? <Icon name="check" size={14} /> : <Icon name="pause" size={14} />}
                  </span>
                </button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
